import Image from "next/legacy/image";
import { useDispatch } from "react-redux";
import Link from "next/link";
import { InferGetStaticPropsType } from "next";

import { Button, Chip, MealCard } from "@chef/components";
import { BASE_64_1X1_GREY, BRAND_PATHS, language } from "@chef/constants";
import { AppShell } from "@chef/feature-app-shell";
import { openModal } from "@chef/state-management";
import {
  getAttribute,
  getImageFromImages,
} from "@chef/state-management/helpers";
import {
  RecipeActionModal,
  RecipeReactionButton,
  RecipeTags,
} from "@chef/smart-components";
import { isNotEmptyArray } from "@chef/utils/array";
import {
  BlockContent,
  ContentComponent,
  useMealboxData,
} from "@chef/feature-sanity";
import { Check } from "@chef/icons/small";
import { slugify } from "@chef/helpers";

import { MealboxGetStaticProps } from "..";

import { intl } from "./MealboxPage.Intl";

export const MealboxPage = ({
  preference,
  recipes,
  allConcepts,
  concept,
}: InferGetStaticPropsType<typeof MealboxGetStaticProps>) => {
  const dispatch = useDispatch();
  const [mealboxContent] = useMealboxData(concept);

  if (!preference || !recipes || !allConcepts) {
    return (
      <ContentComponent block={mealboxContent?.content}>
        {({ children, title, description, imageUrl, index }) => (
          <AppShell
            title={title || intl.MEALBOXES}
            description={description}
            index={index}
            image={imageUrl}
          >
            <AppShell.Meta />
            <AppShell.Drawer />
            <AppShell.Header />
            <AppShell.DiscountBanner />

            <AppShell.Main>{children}</AppShell.Main>

            <AppShell.Footer />
            <AppShell.Sticky anchor="bottom">
              <AppShell.TabBar />
            </AppShell.Sticky>
          </AppShell>
        )}
      </ContentComponent>
    );
  }

  const metaTitle = getAttribute("metaTitle", preference);
  const metaDescription = getAttribute("metaDescription", preference);

  const name = preference.preferenceName;
  const description = preference.description;
  const slug = getAttribute("preference_slug", preference);

  const bulletPoints = preference.attributes
    .filter(
      (a) =>
        [
          "description_bullet_1",
          "description_bullet_2",
          "description_bullet_3",
        ].includes(a.name) && a.value !== "",
    )
    .sort();

  const hasRecipes = isNotEmptyArray(recipes);

  return (
    <AppShell
      title={metaTitle || name}
      description={metaDescription || description}
    >
      <AppShell.Meta />
      <AppShell.Drawer />
      <AppShell.Header />
      <AppShell.DiscountBanner />

      <AppShell.Main grid>
        <div className="flex flex-col items-center gap-6 col-span-full md:col-start-1">
          <div className="flex w-full gap-2 mb-4 overflow-x-scroll md:justify-center scrollbar-hidden">
            {allConcepts.map((p) => (
              <Link href={`./${p.slug}`} key={p.slug} passHref>
                <Chip selected={p.slug === slug}>{p.name}</Chip>
              </Link>
            ))}
          </div>

          <h1>
            <strong>{name}</strong>
          </h1>

          {description && <p className="text-center">{description}</p>}

          <ul className="flex flex-row max-w-screen-lg gap-6 xl:max-w-screen-xl">
            {bulletPoints.map((bp) => (
              <li
                key={bp.name}
                className="flex flex-col items-center w-full gap-4 text-center lg:w-max md:text-start md:flex-row"
              >
                <span className="flex items-center justify-center w-8 h-8 text-white rounded-full shrink-0 bg-primary">
                  <Check />
                </span>
                <span className="w-full text-sm whitespace-pre-line">
                  {bp.value}
                </span>
              </li>
            ))}
          </ul>
        </div>

        <div className="grid grid-cols-2 gap-4 mb-8 col-span-full md:grid-cols-5 xl:grid-cols-5">
          {hasRecipes ? (
            recipes.map((recipe) => {
              const modalRoute = BRAND_PATHS.RECIPE_WITH_NAME_HREF({
                recipeId: recipe.recipeId,
                recipeName: slugify(recipe.recipeName),
              });

              const imageUrl = getImageFromImages({ images: recipe.images });

              return (
                <div className="relative" key={recipe.recipeId}>
                  <MealCard
                    key={recipe.recipeId}
                    className="h-full"
                    language={language}
                    text={recipe.recipeName}
                    tags={<RecipeTags recipe={recipe} />}
                    href={modalRoute}
                    id={recipe.recipeId.toString()}
                    Image={() => (
                      <Image
                        src={imageUrl || BASE_64_1X1_GREY}
                        width={2}
                        height={3}
                        layout="responsive"
                        alt={recipe.recipeName}
                        objectFit="cover"
                        className="rounded"
                        sizes="(max-width: 375px) 50w, (max-width: 768px) 35vw, 20vw"
                      />
                    )}
                    hideFooter
                    onClick={(e) => {
                      e.preventDefault();
                      dispatch(
                        openModal({
                          id: "RECIPE",
                          recipeId: recipe.recipeId,
                          view: "conversion",
                        }),
                      );
                    }}
                  />
                  <RecipeReactionButton
                    recipe={recipe}
                    className="absolute z-10 shadow top-4 right-4"
                  />
                </div>
              );
            })
          ) : (
            <p className="italic text-center col-span-full">
              {intl.NO_RECIPES}
            </p>
          )}
        </div>

        <p className="text-center col-span-full">
          <Link
            href={
              slug
                ? BRAND_PATHS.SIGNUP_CHOOSE_MEALBOX_HREF(slug)
                : BRAND_PATHS.SIGNUP_HREF
            }
          >
            <Button primary>{intl.ORDER_THIS_MEALBOX(name)}</Button>
          </Link>
        </p>

        <p className="text-center col-span-full">
          {intl.SEE_OTHER_WEEKS}{" "}
          <Link
            href={BRAND_PATHS.MY_MENU_LOGOUT_PAGE}
            passHref
            className="underline"
          >
            {intl.HERE}.
          </Link>
        </p>

        {mealboxContent && (
          <div className="col-span-full">
            <BlockContent body={mealboxContent.content?.body} />
          </div>
        )}

        <RecipeActionModal />
      </AppShell.Main>

      <AppShell.Footer />
      <AppShell.Sticky anchor="bottom">
        <AppShell.TabBar />
      </AppShell.Sticky>
    </AppShell>
  );
};
